<template>
  <v-data-table-server
    :headers="vuetifyHeaders"
    :items="items"
    :items-length="totalElements"
    :loading="loading"
    :page="pagination.page + 1"
    :items-per-page="pagination.size"
    :sort-by="vuetifySortBy"
    :item-value="rowKey"
    :items-per-page-options="itemsPerPageOptions"
    :hide-default-footer="disablePagination"
    :mobile="false"
    loading-text="Cargando..."
    no-data-text="No hay registros."
    @update:options="onOptions"
  >
    <template #loading>
      <v-progress-linear color="primary" indeterminate />
    </template>

    <template #item="{ item, index }">
      <tr>
        <td
          v-for="(column, idx) in options"
          :key="idx"
          :class="column.row.type === 'OPTIONS' ? 'text-right' : undefined"
        >
          <!-- número de fila -->
          <span v-if="column.row.type === 'INDEX'">
            {{ pagination.page * pagination.size + index + 1 }}
          </span>

          <!-- enlace -->
          <template v-else-if="column.row.type === 'LINK'">
            <router-link
              v-if="hasValue(item, column)"
              class="text-blue text-decoration-none"
              :to="interpolate(column.row.to, item)"
              :replace="!!column.row.replace"
            >
              <span class="text-no-wrap">
                {{ display(item, column) }}
                <v-icon size="small" color="blue">mdi-open-in-new</v-icon>
              </span>
            </router-link>
            <span v-else>---</span>
          </template>

          <!-- chip, con tooltip opcional -->
          <template v-else-if="column.row.type === 'CHIP'">
            <v-tooltip
              v-if="hasValue(item, column)"
              location="bottom"
              :disabled="!column.row.tooltip"
            >
              <template #activator="{ props: tooltipProps }">
                <v-chip
                  v-bind="tooltipProps"
                  size="small"
                  :label="!!column.row.label"
                  class="text-uppercase text-no-wrap"
                  :color="chipColor(item, column)"
                >
                  {{ display(item, column) }}
                </v-chip>
              </template>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-html="tooltipHtml(item, column)" />
            </v-tooltip>
            <span v-else>---</span>
          </template>

          <!-- menú de acciones -->
          <v-menu v-else-if="column.row.type === 'OPTIONS'" location="bottom end">
            <template #activator="{ props: menuProps }">
              <v-btn
                v-bind="menuProps"
                icon="mdi-dots-vertical"
                variant="text"
                size="small"
              />
            </template>
            <v-list density="compact">
              <v-list-subheader>OPCIONES</v-list-subheader>
              <v-list-item
                v-for="(opt, optIdx) in column.row.options"
                :key="optIdx"
                :disabled="opt.disabled ? opt.disabled(item, column.row) : false"
                @click="execute(opt, item, column)"
              >
                <v-list-item-title class="text-no-wrap">{{ opt.label }}</v-list-item-title>
                <template #append>
                  <v-icon>{{ opt.icon }}</v-icon>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>

          <!-- texto, con tooltip opcional -->
          <v-tooltip v-else location="bottom" :disabled="!column.row.tooltip">
            <template #activator="{ props: tooltipProps }">
              <span v-bind="tooltipProps" class="text-no-wrap">
                {{ display(item, column) }}
              </span>
            </template>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="tooltipHtml(item, column)" />
          </v-tooltip>
        </td>
      </tr>
    </template>
  </v-data-table-server>
</template>

<script setup lang="ts">
/**
 * Tabla configurable por objeto, sobre v-data-table-server.
 *
 * Notas de la migración desde Vuetify 2:
 *
 *  - La paginación server-side ahora es un componente distinto:
 *    v-data-table-server, con `items-length` en vez de `server-items-length`.
 *
 *  - `:options.sync` desapareció. Se usa `@update:options` y se emite un
 *    `Pageable` hacia el padre.
 *
 *  - El modelo de orden cambió de forma (dos arrays paralelos -> un array de
 *    objetos). La conversión contra `sort=campo,asc` de Spring vive en ./sort.ts
 *    con tests propios, porque un error ahí es silencioso.
 *
 *  - Los headers se traducen desde nuestro propio AgDataTableHeader
 *    (`text`/`value`) al de Vuetify (`title`/`key`) acá y en un solo lugar. Eso
 *    evita renombrar ~400 líneas de configuración en nueve vistas.
 *
 *  - AgDataTableRow.vue se pliega en el slot #item. No se reutilizaba en ningún
 *    lado, leía `$route.params` por su cuenta, y separarlo obligaba a pasarle el
 *    array de opciones por props.
 *
 *  - `append` de router-link ya no existe en Vue Router 4+: todas las rutas de
 *    `to` y de `link` tienen que ser ABSOLUTAS.
 */
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { Pageable } from "bancojs";
import type {
  AgDataTableColumnOptions,
  AgDataTableOptions,
  AgDataTableRowMenuOptions,
} from "./types";
import { toPageable, toVuetifySort, type VuetifyTableOptions } from "./sort";

const props = withDefaults(
  defineProps<{
    options: AgDataTableOptions[];
    items: unknown[];
    pagination: Pageable;
    totalElements?: number;
    rowKey?: string;
    loading?: boolean;
    disablePagination?: boolean;
    /**
     * Vuetify ofrece [10, 25, 50, 100] por omisión. Se agrega 5 porque varias de
     * estas colecciones son chicas (llaves de sistema, mapeadores) y con 10 el
     * paginador nunca se ejercita.
     */
    itemsPerPageOptions?: number[];
  }>(),
  {
    totalElements: 0,
    rowKey: "id",
    loading: false,
    disablePagination: false,
    itemsPerPageOptions: () => [5, 10, 25, 50, 100],
  }
);

const emit = defineEmits<{ pagination: [Pageable] }>();

const route = useRoute();
const router = useRouter();

/** Traduce nuestro contrato de header al de Vuetify. */
const vuetifyHeaders = computed(() =>
  props.options.map((opt) => ({
    title: opt.header.text,
    key: opt.header.value,
    sortable: opt.header.sortable ?? true,
    align: opt.header.align,
    width: opt.header.width,
    nowrap: true,
    headerProps: opt.header.class ? { class: opt.header.class } : undefined,
    // `filterable` se acepta en el contrato por compatibilidad pero NO se
    // reenvía: sólo servía al filtrado en cliente, y acá se filtra server-side.
  }))
);

const vuetifySortBy = computed(() => toVuetifySort(props.pagination.sort));

function onOptions(options: VuetifyTableOptions) {
  emit("pagination", toPageable(options, props.rowKey));
}

// ---- helpers de celda ----------------------------------------------------

function rawValue(item: unknown, column: AgDataTableOptions): unknown {
  return (item as Record<string, unknown>)[column.header.value];
}

function hasValue(item: unknown, column: AgDataTableOptions): boolean {
  const v = rawValue(item, column);
  return v !== null && v !== undefined && v !== "";
}

/** El texto a mostrar, aplicando `transform` si la columna lo define. */
function display(item: unknown, column: AgDataTableOptions): string {
  const value = rawValue(item, column);
  const row = column.row as Extract<
    AgDataTableColumnOptions,
    { transform?: (val: unknown) => string }
  >;
  if (typeof row.transform === "function") return row.transform(value);
  return value === null || value === undefined ? "" : String(value);
}

function chipColor(item: unknown, column: AgDataTableOptions): string | undefined {
  if (column.row.type !== "CHIP") return undefined;
  const key = String(rawValue(item, column));
  return column.row.color[key] || undefined;
}

function tooltipHtml(item: unknown, column: AgDataTableOptions): string {
  const row = column.row as Extract<
    AgDataTableColumnOptions,
    { tooltipContent?: (val: unknown) => string }
  >;
  return typeof row.tooltipContent === "function"
    ? row.tooltipContent(rawValue(item, column))
    : "";
}

/**
 * Interpola `{campo}` contra el item y `{$param}` contra los params de la ruta.
 *
 * El resultado tiene que ser una ruta ABSOLUTA: sin el prop `append` de Vue
 * Router 3, una ruta relativa ya no se resuelve contra la ruta actual — navegaría
 * a un destino equivocado sin error alguno.
 */
function interpolate(expression: string, item: unknown): string {
  let out = String(expression);
  const tokens = out.match(/{(.*?)}+/g);
  if (!tokens) return out;
  for (const token of tokens) {
    const prop = token.replace(/[{}]/g, "");
    const replacement = prop.startsWith("$")
      ? String(route.params[prop.slice(1)] ?? "")
      : String((item as Record<string, unknown>)[prop] ?? "");
    out = out.replace(token, replacement);
  }
  return out;
}

function execute(
  opt: AgDataTableRowMenuOptions,
  item: unknown,
  column: AgDataTableOptions
) {
  if (opt.link) {
    router.push({ path: interpolate(opt.link, item), replace: !!opt.replace });
  } else if (opt.action) {
    opt.action(item, column.row);
  }
}
</script>
